import { parseDate, type ImportRow } from '@daghfal/core';

/**
 * Reads a spreadsheet saved as CSV for the bulk import (FR-ADM-21, FR-ADM-22).
 *
 * RFC 4180 as spreadsheets write it: commas between fields, a field in double quotes may hold commas, line breaks
 * and doubled quotes, and lines end in CRLF or LF. A UTF-8 byte-order mark, which Excel writes for Arabic, is
 * dropped. A blank line is kept as a row of empty cells, so each row stays at the index the spreadsheet numbers it
 * by, a field that spans lines included.
 */
export function parseCsv(text: string): string[][] {
  const source = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const table: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;

  const endField = () => { row.push(field); field = ''; };
  const endRow = () => {
    endField();
    table.push(row);
    row = [];
  };

  for (let index = 0; index < source.length; index += 1) {
    const char = source[index]!;
    if (quoted) {
      if (char === '"') {
        if (source[index + 1] === '"') { field += '"'; index += 1; } else quoted = false;
      } else {
        field += char;
      }
    } else if (char === '"' && field === '') {
      quoted = true;
    } else if (char === ',') {
      endField();
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && source[index + 1] === '\n') index += 1;
      endRow();
    } else {
      field += char;
    }
  }
  if (field !== '' || row.length > 0) endRow();

  return table;
}

type ImportField = Exclude<keyof ImportRow, 'rowNumber'>;

const isBlank = (cells: string[]) => cells.every((cell) => cell.trim() === '');

/** Every column the import reads, by the header the template writes and the Arabic one a nursery may use. */
export const IMPORT_COLUMNS: { field: ImportField; headers: string[] }[] = [
  { field: 'firstName', headers: ['firstName', 'الاسم الأول'] },
  { field: 'lastName', headers: ['lastName', 'اسم العائلة'] },
  { field: 'dateOfBirth', headers: ['dateOfBirth', 'تاريخ الميلاد'] },
  { field: 'gender', headers: ['gender', 'الجنس'] },
  { field: 'className', headers: ['className', 'الصف'] },
  { field: 'enrollmentDate', headers: ['enrollmentDate', 'تاريخ التسجيل'] },
  { field: 'guardianName', headers: ['guardianName', 'اسم ولي الأمر'] },
  { field: 'guardianPhone', headers: ['guardianPhone', 'هاتف ولي الأمر'] },
  { field: 'guardianRelationship', headers: ['guardianRelationship', 'صلة ولي الأمر'] },
  { field: 'emergencyContactName', headers: ['emergencyContactName', 'اسم جهة الطوارئ'] },
  { field: 'emergencyContactPhone', headers: ['emergencyContactPhone', 'هاتف جهة الطوارئ'] },
  { field: 'emergencyContactRelationship', headers: ['emergencyContactRelationship', 'صلة جهة الطوارئ'] },
  { field: 'allergies', headers: ['allergies', 'الحساسيات'] },
];

const BY_HEADER = new Map(IMPORT_COLUMNS.flatMap(({ field, headers }) => headers.map((header) => [fold(header), field])));

const GENDERS: Record<string, string> = { 'أنثى': 'Female', 'ذكر': 'Male', 'بنت': 'Female', 'ولد': 'Male' };

function fold(header: string): string {
  return header.trim().toLowerCase();
}

/**
 * A day-first date as the API takes it, `YYYY-MM-DD`. The server reads any other form with the invariant culture,
 * month first, so `3/4/2023` would become the fourth of March (SRS §2.4); it is converted here, day first, instead.
 */
function isoDate(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  return parseDate(value) ?? value;
}

/**
 * The table's rows as the import request takes them, numbered as the spreadsheet numbers them (the first row is 1,
 * blank rows counted and left out), and the headers it did not recognise, so the screen can say which columns were
 * left out. The header is the first row that is not blank.
 */
export function toImportRows(table: string[][]): { rows: ImportRow[]; unknownColumns: string[] } {
  const headerIndex = table.findIndex((cells) => !isBlank(cells));
  const header = table[headerIndex] ?? [];
  const fields = header.map((name) => BY_HEADER.get(fold(name)) ?? null);
  const unknownColumns = header.filter((name, index) => fields[index] === null && name.trim() !== '');

  const body = table
    .map((cells, index) => ({ cells, rowNumber: index + 1 }))
    .slice(headerIndex + 1)
    .filter(({ cells }) => !isBlank(cells));

  const rows = body.map(({ cells, rowNumber }) => {
    const row: ImportRow = {
      rowNumber,
      firstName: null, lastName: null, dateOfBirth: null, gender: null, className: null, enrollmentDate: null,
      guardianName: null, guardianPhone: null, guardianRelationship: null,
    };
    fields.forEach((field, column) => {
      const value = cells[column]?.trim() ?? '';
      if (!field || value === '') return;
      row[field] = field === 'dateOfBirth' || field === 'enrollmentDate' ? isoDate(value)
        : field === 'gender' ? GENDERS[value] ?? value
        : value;
    });
    return row;
  });

  return { rows, unknownColumns };
}

/** A chosen file's text, as UTF-8: `Blob.text` where the browser has it, a `FileReader` where it does not. */
export function readText(file: Blob): Promise<string> {
  if (typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('unreadable'));
    reader.readAsText(file, 'utf-8');
  });
}
